import { ApiService } from './ApiService';
import type { IGetOneResponse } from '../util/Types/Generic';

const pathPrefix = '/integrations';

export type ConnectionMode = 'oauth' | 'credentials' | 'webhook';

export interface IntegrationConnectionDTO {
  id: string;
  name: string;
  provider: string;
  ownerUserId: string;
  ownerUserName: string;
  isOwn: boolean;
}

export interface ConnectResult {
  success: boolean;
  integrationId: string;
  name: string;
}

export class ThirdPartyIntegrationService extends ApiService {
  static getConnectionMode = (
    provider: string,
  ): Promise<IGetOneResponse<{ connectionMode: ConnectionMode }>> => {
    return this.get(
      `${pathPrefix}/${provider}/mode`,
    ) as Promise<IGetOneResponse<{ connectionMode: ConnectionMode }>>;
  };

  static getInstallUrl = (
    provider: string,
  ): Promise<IGetOneResponse<{ installUrl: string }>> => {
    return this.get(
      `${pathPrefix}/${provider}/install`,
    ) as Promise<IGetOneResponse<{ installUrl: string }>>;
  };

  static getConnections = (
    provider: string,
  ): Promise<IGetOneResponse<IntegrationConnectionDTO[]>> => {
    return this.get(
      `${pathPrefix}/${provider}/connections`,
    ) as Promise<IGetOneResponse<IntegrationConnectionDTO[]>>;
  };

  static connect = (
    provider: string,
    body: Record<string, unknown>,
  ): Promise<IGetOneResponse<ConnectResult>> => {
    return this.post(`${pathPrefix}/${provider}/connect`, body) as Promise<
      IGetOneResponse<ConnectResult>
    >;
  };

  static completeOAuth = (
    provider: string,
    code: string,
    connectionName?: string,
  ): Promise<IGetOneResponse<ConnectResult>> => {
    return this.post(`${pathPrefix}/${provider}/oauth-callback`, {
      code,
      connectionName,
    }) as Promise<IGetOneResponse<ConnectResult>>;
  };

  static oauthDeny = (
    provider: string,
    state?: string,
  ): Promise<IGetOneResponse<{ success: boolean }>> => {
    return this.post(`${pathPrefix}/${provider}/oauth-deny`, {
      ...(state ? { state } : {}),
    }) as Promise<IGetOneResponse<{ success: boolean }>>;
  };

  static getConnectionStatus = (provider: string): Promise<
    IGetOneResponse<{
      status: 'pending' | 'completed';
    }>
  > => {
    return this.get(`${pathPrefix}/${provider}/status`) as Promise<
      IGetOneResponse<{ status: 'pending' | 'completed' }>
    >;
  };

  static disconnect = (
    provider: string,
    integrationId?: string,
  ): Promise<IGetOneResponse<unknown>> => {
    return this.post(`${pathPrefix}/${provider}/disconnect`, {
      ...(integrationId ? { integrationId } : {}),
    });
  };

  static refreshToken = (
    provider: string,
    integrationId?: string,
  ): Promise<IGetOneResponse<unknown>> => {
    return this.post(`${pathPrefix}/${provider}/refresh-token`, {
      ...(integrationId ? { integrationId } : {}),
    });
  };

  static pullData = <T = unknown>(
    provider: string,
    config: Record<string, unknown>,
  ): Promise<IGetOneResponse<T>> => {
    return this.post(`${pathPrefix}/${provider}/pull`, config) as Promise<
      IGetOneResponse<T>
    >;
  };

  static pushData = <T = unknown>(
    provider: string,
    config: Record<string, unknown>,
  ): Promise<IGetOneResponse<T>> => {
    return this.post(`${pathPrefix}/${provider}/push`, config) as Promise<
      IGetOneResponse<T>
    >;
  };

  static testPush = <T = unknown>(
    provider: string,
    config: Record<string, unknown>,
  ): Promise<IGetOneResponse<T>> => {
    return this.post(`${pathPrefix}/${provider}/test-push`, config) as Promise<
      IGetOneResponse<T>
    >;
  };

  static getDynamicOptions = (
    provider: string,
    fieldName: string,
    context?: Record<string, unknown>,
  ): Promise<IGetOneResponse<{ label: string; value: string }[]>> => {
    const params = context ? { ...context } : {};
    return this.get(
      `${pathPrefix}/${provider}/options/${fieldName}`,
      undefined,
      undefined,
      params as Record<string, string | number>,
    ) as Promise<IGetOneResponse<{ label: string; value: string }[]>>;
  };

  static getDatabaseSchema = (
    provider: string,
    databaseOrRecordId: string,
    context?: Record<string, unknown> & { isRecordId?: boolean },
  ): Promise<
    IGetOneResponse<{
      properties: Array<{
        id: string;
        name: string;
        type: string;
        options?: Array<{ id: string; name: string }>;
      }>;
    }>
  > => {
    const isRecordId = context?.isRecordId === true;
    const params: Record<string, string | number | boolean> = {
      ...(isRecordId ? { recordId: databaseOrRecordId } : { databaseId: databaseOrRecordId }),
      ...(context as Record<string, string | number>),
    };
    delete (params as Record<string, unknown>).isRecordId;
    return this.get(
      `${pathPrefix}/${provider}/schema/database`,
      undefined,
      undefined,
      params,
    ) as Promise<
      IGetOneResponse<{
        properties: Array<{
          id: string;
          name: string;
          type: string;
          options?: Array<{ id: string; name: string }>;
        }>;
      }>
    >;
  };
}
